import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  dashboardDataQuery,
  departmentUsersQuery,
  performanceDashboardQuery,
  type DashboardData,
} from '../../api/easeedeskPerformance'
import { moduleOf, shellQuery } from '../../api/shell'
import { OdbBoard, OdbCard } from '../../dashboard/OdbCard'
import { SettingsCheck } from '../../ui/SettingsParts'
import { usePageStyles } from '../../ui/usePageStyles'
import { durationString } from '../format'
import { BarChart, COLORS, DoughnutChart, LineChart, PieChart } from './MyPerformanceCharts'
import { RANGES, rangeOf, type RangeName } from './range'

/**
 * Tabloul "My Performance" - `easeedesk-dashboards/My_performance`, machetele
 * `views/version-3/dashboards/easeedesk/my_performance/{index,cards,
 * closed_tickets_cards,my_team}.php` si
 * `public/js/dashboards/easeedesk/my_performance.js`.
 *
 * ASTA E "MY PERFORMANCE"-UL DIN MENIU. In `routes.php`, adresa
 * `easeedesk/my_performance` duce aici, nu la `Easeedesk::my_performance` -
 * ecranul acela, cu coloana din stanga si Completion Time, sta la
 * `/app/easeedesk/my_performance/completion_time` (`MyPerformancePage`).
 *
 * -----------------------------------------------------------------------------
 * DE CE ARATA ALTFEL DECAT IERI: ERA ULTIMUL TABLOU RAMAS PE DESENUL VECHI
 * -----------------------------------------------------------------------------
 *
 * Pe 11 septembrie 2026 toate celelalte tablouri ale portalului statusera deja
 * pe desenul de "board": cele cinci de carduri (Platform si cele de modul),
 * Endpoint Performance, SNMP, Core Applications si Applications Dashboard.
 * Asta ramasese singurul cu panouri `panel panel-default`, dale Metronic si
 * randuri Bootstrap - adica al doilea limbaj pentru acelasi lucru, pe acelasi
 * portal, la o apasare de meniu distanta unul de altul.
 *
 * Deci se ia DESENUL si se lasa masinaria, exact ca la vecini: `OdbBoard` si
 * `OdbCard` din `dashboard/OdbCard.tsx` - aceeasi foaie (`od_dashboard.css`),
 * aceeasi grila de patru coloane, acelasi cap de card cu placuta modulului,
 * aceeasi cifra de 40px. NU se scrie aici niciun hexa si nicio masura de card:
 * daca maine se schimba rotunjimea tablourilor, se schimba si asta, singura.
 *
 * `OdbCard`, si nu `BoardCard`, fiindca ecranul asta isi DESENEAZA singur cele
 * cinci grafice (SVG, in `MyPerformanceCharts.tsx`): cardul trebuie sa primeasca
 * COPII si sa spuna cata inaltime a ramas sub cap, nu un sir de HTML din
 * catalog. E chiar rostul pentru care a fost scris `OdbCard` - vezi capul lui.
 *
 * CIFRELE SUNT ACELEASI, din acelasi capat de API. Nu s-a adaugat niciuna si
 * nu s-a scos niciuna; s-a schimbat numai haina:
 *
 *   - panourile devin carduri in grila. Cele cinci cifre ale tichetelor vii au
 *     fiecare cardul ei; cele patru ale tichetelor inchise si cele trei locuri
 *     din echipa stau in cate o LISTA (`.odb-rows`), forma pe care o au listele
 *     de pe celelalte tablouri;
 *   - barele de progres Bootstrap devin rigla zonei (`.odb-scale`) la cifrele
 *     singure si bara din spatele randului (`.odb-row-fill`) in liste. Aceleasi
 *     impartiri, pana la ultimul procent - vezi `percent()` mai jos;
 *   - panoul albastru "My Position In Team" si cel gri-inchis "Closed Tickets"
 *     raman fara culoarea lor de fundal. In zona asta culoarea inseamna STARE,
 *     si un panou albastru pus ca sa se vada ca e altceva ar fi spus, aici, ca
 *     ceva e in regula. Numele cardului spune acum ce spunea culoarea.
 *
 * TREI LUCRURI ERAU DEJA ALTFEL DECAT IN PORTAL, SI RAMAN ASA:
 *
 *  - PERIODA se alege din scurtaturi, nu dintr-un `daterangepicker`. Sunt
 *    exact cele sase din script (Today ... Last Month), plus cea comuna
 *    tuturor tablourilor v4. Calendarul cu zile alese de mana ar cere
 *    biblioteca, care nu e in v4 - la fel ca la Leader Board si la celelalte.
 *    Acum sunt pastilele din bara, ca ferestrele de timp ale celorlalte
 *    tablouri, nu o casuta de formular;
 *  - NU E BUTONUL "Apply". In portal, alegerea din casute nu facea nimic
 *    pana nu se apasa; aici ecranul se reincarca singur la fiecare schimbare;
 *  - CIFRELE NU URCA ANIMAT (`animateNumber`). Se scriu direct.
 *
 * SI UNUL CARE RAMANE: "Resolved" din Closed Tickets e mereu egal cu felia
 * "Closed" a placintei, fiindca `userCountResolvedTickets` si
 * `userCountClosedTickets` au in portal aceeasi interogare, litera cu litera.
 *
 * -----------------------------------------------------------------------------
 * FILTRELE IES DIN SERTARUL VERDE SI INTRA IN BARA
 * -----------------------------------------------------------------------------
 *
 * Departamentul, omul si bifa "All Active Tickets" stateau intr-un sertar care
 * se deschidea din dreapta (`page-quick-sidebar`, verde inchis, cu butonul lui
 * "Filters") - ultima bucata de Metronic de pe ecran. Pe desenul tablourilor ce
 * se alege sta in bara de sus, la vedere, langa fereastra de timp: asa e si la
 * Endpoint Performance, si la celelalte. Sunt aceleasi trei casute, cu aceleasi
 * nume si acelasi comportament (departament nou => casuta de oameni se
 * goleste), doar ca nu mai trebuie deschis nimic ca sa se vada pe ce te uiti.
 */

/**
 * Latimea unei bare de progres. Scriptul vechi imparte fara sa se uite la
 * numitor (`parseInt(a / 0 * 100)` da NaN, iar `width: NaN%` e sarit de
 * browser si bara ramane la zero); aici zero inseamna zero, pe fata.
 */
function percent(part: number, whole: number): string {
  const value = whole > 0 ? Math.trunc((part / whole) * 100) : 0

  return `${Math.max(0, Math.min(100, value))}%`
}

/**
 * Cifra si rigla ei: un card mic din zona tablourilor.
 *
 * Rigla ramane in cerneala obisnuita a zonei, fara culoare. La Endpoint
 * Performance riglele sunt colorate fiindca acolo culoarea e CHEIA cu care se
 * citesc gogoasa si stiva de langa ele - portocaliu procesor, verde ram. Aici
 * n-are ce lega: "Escalated" si "Planned" nu apar in niciun grafic de pe ecran,
 * iar rosul si albastrul pe care le purtau in portal erau doar doua culori
 * diferite, ca sa nu semene barele intre ele. Si NU primesc `tone`: n-avem
 * pragul peste care "trei escaladate" e rau, iar o culoare de stare pusa fara
 * prag e o parere, nu o masuratoare.
 */
function FigureCard({
  icon,
  title,
  id,
  value,
  part,
  whole,
  of,
  note,
}: {
  icon: string
  title: string
  id: string
  value: React.ReactNode
  /** Cand exista amandoua, se deseneaza rigla `part / whole` si se scrie sub ea. */
  part?: number
  whole?: number
  of?: string
  /** Randul de sub cifra, la cardurile fara rigla. */
  note?: string
}) {
  return (
    <OdbCard module="easeedesk" icon={icon} title={title} w={1} h={1} value={<span id={id}>{value}</span>}>
      {part === undefined || whole === undefined ? (
        <div className="odb-of">{note}</div>
      ) : (
        <>
          <span className="odb-scale">
            <span className="odb-scale-fill" style={{ width: percent(part, whole) }} />
          </span>
          <div className="odb-ring-say">
            <span className="odb-of">
              {part} of {whole} {of} · {percent(part, whole)}
            </span>
          </div>
        </>
      )}
    </OdbCard>
  )
}

/** Un rand de lista, cu bara din spate cat spune cifra lui. */
function Row({ label, id, value, width }: { label: string; id: string; value: React.ReactNode; width: string }) {
  return (
    <div className="odb-row">
      <span className="odb-row-fill" style={{ width }} />
      <span className="odb-row-label">{label}</span>
      <span className="odb-row-value" id={id}>
        {value}
      </span>
    </div>
  )
}

/**
 * Tichetele inchise: patru cifre cu aceleasi impartiri ca `setClosedProgressBar`.
 *
 * Doua se raporteaza la `resolved`, doua la suma `resolved + escalated`. Nu e o
 * scapare a portalului si nu se "indreapta" aici: "cate dintre cele rezolvate au
 * fost luate in primire de mine" si "cat din ce am inchis a plecat mai departe"
 * sunt doua intrebari diferite, cu doi numitori diferiti.
 */
function ClosedCard({ data }: { data: DashboardData }) {
  const closed = data.closed_tickets
  const both = closed.resolved + closed.escalated

  return (
    <OdbCard module="easeedesk" icon="fa-check-double" title="Closed Tickets" w={1} h={2} id="closedTickets">
      <div className="odb-rows">
        <Row label="Tookover" id="closed-tookover" value={closed.tookover} width={percent(closed.tookover, closed.resolved)} />
        <Row label="Resolved" id="closed-resolved" value={closed.resolved} width={percent(closed.resolved, both)} />
        <Row label="Escalated" id="closed-escalated" value={closed.escalated} width={percent(closed.escalated, both)} />
        <Row label="Reopened" id="closed-reopened" value={closed.reopened} width={percent(closed.reopened, closed.resolved)} />
      </div>
    </OdbCard>
  )
}

/** "n/a" era raspunsul portalului cand omul nu apare in clasamentul de rezolvate. */
function place(value: number | null): string {
  return value === null ? 'n/a' : String(value)
}

/**
 * Locul meu in echipa, pe cele trei clasamente.
 *
 * Bara din spatele randului lipseste aici, cu buna stiinta: un LOC nu e o parte
 * din ceva - locul 2 nu inseamna "doua treimi". Randurile raman randuri de
 * lista, cu cifra la dreapta, ca peste tot.
 */
function TeamCard({ data }: { data: DashboardData }) {
  const position = data.team.position

  return (
    <OdbCard module="easeedesk" icon="fa-medal" title="My Position In Team" w={1} h={1} id="teamPosition">
      <div className="odb-rows">
        <Row label="Tookover" id="team-position-tookover" value={place(position.tookover)} width="0%" />
        <Row label="Active" id="team-position-active" value={place(position.active)} width="0%" />
        <Row label="Resolved" id="team-position-resolved" value={place(position.resolved)} width="0%" />
      </div>
    </OdbCard>
  )
}

const EMPTY: DashboardData = {
  ready: false,
  active_tickets: { total: 0, tookover: 0, identified: 0, escalated: 0, planned: 0 },
  closed_tickets: { tookover: 0, resolved: 0, escalated: 0, reopened: 0 },
  active: 0,
  active_daily: [],
  closed: 0,
  top: [],
  sla: { hit: 0, miss: 0 },
  team: { active: 0, closed: 0, position: { active: 0, resolved: null, tookover: 0 } },
}

export function MyPerformanceDashboardPage() {
  const [group, setGroup] = useState(0)
  const [user, setUser] = useState(0)
  const [rangeName, setRangeName] = useState<RangeName>('Today')
  /* Bifa e pusa la deschidere, ca in macheta - deci ecranul porneste nefiltrat. */
  const [allTickets, setAllTickets] = useState(true)

  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(['css/od_dashboard.css'], shell.data?.version ?? '')

  const page = useQuery(performanceDashboardQuery).data
  const users = useQuery(departmentUsersQuery(group)).data?.users ?? []
  const range = rangeOf(rangeName)
  const data = useQuery(dashboardDataQuery({ allTickets, from: range.from, to: range.to, user, group })).data ?? EMPTY

  const active = data.active_tickets

  const bar = (
    <div className="odb-bar-top">
      <h1 className="odb-name">My Performance</h1>
      <nav className="odb-views" aria-label="Date range">
        {RANGES.map((name) => (
          <button
            type="button"
            key={name}
            className={`odb-view${name === rangeName ? ' is-on' : ''}`}
            onClick={() => setRangeName(name)}
          >
            {name}
          </button>
        ))}
      </nav>
      {/*
        `form-inline` pe langa `.odb-bar-right`, ca la Endpoint Performance:
        `form-control` din Bootstrap e `display: block; width: 100%`, iar intr-un
        rand flex asta ar intinde casutele cat tine bara.
      */}
      <div className="odb-bar-right form-inline">
        <label htmlFor="department">Department</label>
        <select
          id="department"
          className="form-control input-sm"
          value={group}
          onChange={(e) => {
            setGroup(Number(e.target.value))
            /* Departament nou, casuta de oameni se goleste - ca in
               `select.select-event` din macheta. */
            setUser(0)
          }}
        >
          <option value={0}>no department selected...</option>
          {(page?.departments ?? []).map((one) => (
            <option key={one.id} value={one.id}>
              {one.name}
            </option>
          ))}
        </select>
        <label htmlFor="admins">User</label>
        <select
          id="admins"
          className="form-control input-sm"
          value={user}
          onChange={(e) => setUser(Number(e.target.value))}
        >
          <option value={0}>no user selected...</option>
          {users.map((one) => (
            <option key={one.id} value={one.id}>
              {one.name}
            </option>
          ))}
        </select>
        <SettingsCheck id="check_all_tickets" checked={allTickets} onChange={setAllTickets} label="All Active Tickets" />
      </div>
    </div>
  )

  return (
    <OdbBoard bar={bar}>
      <FigureCard
        icon="fa-ticket-alt"
        title="Active Tickets"
        id="active-total"
        value={active.total}
        note="tickets still open"
      />
      <FigureCard
        icon="fa-level-up-alt"
        title="Escalated"
        id="active-escalated"
        value={active.escalated}
        part={active.escalated}
        whole={active.total}
        of="active"
      />
      <FigureCard
        icon="fa-calendar-check"
        title="Planned"
        id="active-planned"
        value={active.planned}
        part={active.planned}
        whole={active.total}
        of="active"
      />
      <FigureCard
        icon="fa-hand-paper"
        title="Average Tookover Time"
        id="active-tookover"
        value={durationString(active.tookover)}
        note="from raised to taken over"
      />
      <FigureCard
        icon="fa-search"
        title="Average Identified Time"
        id="active-identified"
        value={durationString(active.identified)}
        note="from raised to cause identified"
      />

      <OdbCard module="easeedesk" icon="fa-chart-bar" title="Top Ticket Types" w={2} h={2} id="topTicketTypes">
        {(height) => (
          <div style={{ height }}>
            <BarChart
              label="# user active tickets"
              bars={data.top.map((one) => ({
                label: one.status_name,
                value: one.count,
                color: one.status_color,
              }))}
            />
          </div>
        )}
      </OdbCard>

      <OdbCard module="easeedesk" icon="fa-chart-pie" title="Active / Closed" w={1} h={2} id="activeClosed">
        {(height) => (
          <div style={{ height }}>
            <PieChart
              title="Active / Closed"
              slices={[
                { label: 'Active', value: data.active, color: COLORS.blue },
                { label: 'Closed', value: data.closed, color: COLORS.red },
              ]}
            />
          </div>
        )}
      </OdbCard>

      <OdbCard module="easeedesk" icon="fa-bullseye" title="SLA Procent" w={1} h={2} id="slaPercent">
        {(height) => (
          <div style={{ height }}>
            <DoughnutChart
              centerText="SLA Hit"
              centerColor="#36A2EB"
              slices={[
                { label: 'Hit', value: data.sla.hit, color: COLORS.blue },
                { label: 'Miss', value: data.sla.miss, color: COLORS.red },
              ]}
            />
          </div>
        )}
      </OdbCard>

      <ClosedCard data={data} />

      <OdbCard module="easeedesk" icon="fa-chart-line" title="Tickets / hours" w={2} h={2} id="ticketsHours">
        {(height) => (
          <div style={{ height }}>
            <LineChart
              labels={data.active_daily.map((point) => point.created)}
              series={[
                { label: 'Active Tickets', color: COLORS.red, values: data.active_daily.map((p) => p.active) },
                { label: 'Progress Tickets', color: COLORS.blue, values: data.active_daily.map((p) => p.progress) },
                { label: 'Resolved Tickets', color: COLORS.green, values: data.active_daily.map((p) => p.resolved) },
              ]}
            />
          </div>
        )}
      </OdbCard>

      <OdbCard module="easeedesk" icon="fa-users" title="Active / Closed (My Team)" w={1} h={2} id="teamActiveClosed">
        {(height) => (
          <div style={{ height }}>
            <PieChart
              title="Active / Closed (My Team)"
              slices={[
                { label: 'Active', value: data.team.active, color: COLORS.red },
                { label: 'Closed', value: data.team.closed, color: COLORS.blue },
              ]}
            />
          </div>
        )}
      </OdbCard>

      <TeamCard data={data} />
    </OdbBoard>
  )
}
